import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Loader2, SearchIcon, X } from "lucide-react";
import CloseButton from "../assets/CloseButton";
import SmallLoadingSpinner from "../assets/SmallLoadingSpinner";
import { useItemCache } from "../context/ItemCacheContext";
import { Item } from "../types/Types";

interface CachedItemSelectByCodeProps {
  value?: string;
  displayValue?: string; // Direct display value - skips cache lookup for fast rendering
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  label?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  enableRef?: boolean;
  disabled?: boolean;
}

const CachedItemSelectByCode = forwardRef<HTMLInputElement, CachedItemSelectByCodeProps>(
  ({ value, displayValue, onChange, placeholder, required = false, onKeyDown, enableRef = false, label, disabled = false }, ref) => {
  const { getItem, searchItems, isLoading, isPreloading } = useItemCache();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [filteredItems, setFilteredItems] = useState<Item[]>([]);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isWaitingForCache, setIsWaitingForCache] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const ignoreBlur = useRef(false);
    const isTypingRef = useRef(false);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Fetch item by value if not in cache
    useEffect(() => {
      // If user is actively typing a new code, don't overwrite the input
      if (isTypingRef.current) return;
      if (!value) {
        setSearchTerm("");
        return;
      }
      // If displayValue is provided, use it directly (fast path - no API calls)
      if (displayValue !== undefined) {
        setSearchTerm(displayValue);
        return;
      }
      const item = getItem(value);
      if (item) {
        setSearchTerm(item.ident || "");
      } else if (!isPreloading) {
        // Show loading state while waiting for cache
        setIsWaitingForCache(true);

        // Wait 300ms for bulk preload to potentially start/complete before making individual call
        const timeoutId = setTimeout(() => {
          // Check cache again after delay
          const cachedItem = getItem(value);
          if (cachedItem) {
            if (!isTypingRef.current) {
              setSearchTerm(cachedItem.ident || "");
            }
            setIsWaitingForCache(false);
            return;
          }

          // Only make individual API call if still not in cache
          const apiUrl = process.env.REACT_APP_API_URL ?? '';
          const token = localStorage.getItem('token');
          fetch(`${apiUrl}/api/items/${value}`, { headers: { 'x-auth-token': token ?? '' } })
            .then(res => res.json())
            .then(data => {
              if (!isTypingRef.current) {
                if (data && data._id && data.ident) {
                  setSearchTerm(data.ident);
                } else {
                  setSearchTerm("");
                }
              }
            })
            .catch(() => { if (!isTypingRef.current) setSearchTerm(""); })
            .finally(() => setIsWaitingForCache(false));
        }, 300);

        return () => {
          clearTimeout(timeoutId);
          setIsWaitingForCache(false);
        };
      }
      // If isPreloading is true, wait for it to finish - the useEffect will re-run when isPreloading changes
    }, [value, displayValue, getItem, isPreloading]);

    // Reset typing flag when dropdown closes
    useEffect(() => {
      if (!isOpen) {
        isTypingRef.current = false;
      }
    }, [isOpen]);

    // Debounced update of filtered items when search term changes or dropdown opens
    useEffect(() => {
      if (isOpen) {
        const handler = setTimeout(async () => {
          try {
            // For by-code search, prioritize ident field in the search
            const result = await searchItems(searchTerm, 1);
            // Filter client-side to prioritize ident matches
            let filteredByCode = result.items.filter(item =>
              !searchTerm || item.ident?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            // If no results and searchTerm is non-empty, try direct API fetch for that code
            if (filteredByCode.length === 0 && searchTerm.trim()) {
              const apiUrl = process.env.REACT_APP_API_URL ?? '';
              const token = localStorage.getItem('token');
              try {
                const res = await fetch(`${apiUrl}/api/items/by-code/${encodeURIComponent(searchTerm.trim())}`, {
                  headers: { 'x-auth-token': token ?? '' }
                });
                const data = await res.json();
                if (data && data._id && data.ident) {
                  filteredByCode = [{ _id: data._id, ident: data.ident, name: data.name }];
                }
              } catch (err) {
                // Ignore fetch error, just show no results
              }
            }

            setFilteredItems(filteredByCode);
            setHasMore(result.hasMore);
            setCurrentPage(1);
            // Auto-highlight first item when results load
            if (filteredByCode.length > 0) {
              setHighlighted(0);
            }
          } catch (error) {
            console.error('Error searching items by code:', error);
            setFilteredItems([]);
            setHasMore(false);
          }
        }, 300); // 300ms debounce
        return () => clearTimeout(handler);
      }
    }, [searchTerm, isOpen, searchItems]);

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          !inputRef.current?.contains(e.target as Node) &&
          !dropdownRef.current?.contains(e.target as Node)
        ) {
          setIsOpen(false);
          setHighlighted(-1);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
      if (highlighted >= 0 && dropdownRef.current) {
        const item = dropdownRef.current.children[highlighted] as HTMLElement;
        item?.scrollIntoView({ block: "nearest" });
      }
    }, [highlighted]);

    const updateDropdownPosition = () => {
      if (inputRef.current && label === false) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    const loadMoreItems = async () => {
      if (!hasMore || isLoadingMore) return;

      setIsLoadingMore(true);
      try {
        const result = await searchItems(searchTerm, currentPage + 1);
        // Filter client-side to prioritize ident matches
        const filteredByCode = result.items.filter(item =>
          !searchTerm || item.ident?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filteredByCode.length > 0) {
          setFilteredItems(prev => [...prev, ...filteredByCode]);
          setCurrentPage(prev => prev + 1);
          // Note: No scroll restoration needed - browser handles this correctly
          // when appending items to a scrollable list
        }
        setHasMore(result.hasMore);
      } catch (error) {
        setHasMore(false);
      } finally {
        setIsLoadingMore(false);
      }
    };


    const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
      const element = e.currentTarget;
      // Use 100px threshold to trigger load before reaching absolute bottom
      // This provides smoother UX and reduces scroll jump issues during fast scrolling
      const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 100;

      // Only load more if we're near bottom, have more items, and not currently loading more
      if (isNearBottom && hasMore && !isLoadingMore) {
        loadMoreItems();
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      isTypingRef.current = true;
      setSearchTerm(val);
      if (val === "") {
        onChange("");
        setHighlighted(-1);
        setIsOpen(true); // keep dropdown open ready for new search
        if (label === false) updateDropdownPosition();
        return;
      }
      if (!isOpen) setIsOpen(true);
      setHighlighted(-1);
      if (label === false) updateDropdownPosition();
    };

    const handleSelect = (item: Item) => {
      onChange(item._id);
      setSearchTerm(item.ident || "");
      setIsOpen(false);
      isTypingRef.current = false;
    };

    const handleClear = () => {
      isTypingRef.current = true; // treat as start of new typing session
      onChange("");
      setSearchTerm("");
      setHighlighted(-1);
      setIsOpen(true);
      if (label === false) updateDropdownPosition();
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle dropdown-specific keys first
      if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      // Handle navigation and selection within the dropdown
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlighted((i) => Math.min(i + 1, filteredItems.length - 1));
          return; // Don't call parent handler for navigation
        case "ArrowUp":
          e.preventDefault();
          setHighlighted((i) => Math.max(i - 1, -1));
          return; // Don't call parent handler for navigation
        case "Enter":
          // If dropdown is open and an item is highlighted, select it
          if (isOpen && highlighted >= 0 && filteredItems[highlighted]) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(filteredItems[highlighted]);
            return; // Don't call parent handler after selection
          } else if (isOpen && filteredItems.length === 0) {
            e.preventDefault();
            setSearchTerm("");
            return; // Don't call parent handler for clearing
          }
          // If dropdown is closed or no item highlighted, let parent handle Enter (table save)
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setHighlighted(-1);
          return; // Don't call parent handler for escape
      }

      // Call parent onKeyDown handler for other keys (like Enter when dropdown is closed)
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    // Render compact mode for tables
    if (label === false) {
      return (
        <div className="w-full h-6">
          <div className="relative h-6">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                if (!disabled) {
                  setIsOpen(true);
                  updateDropdownPosition();
                }
              }}
              onBlur={() => {
                if (!ignoreBlur.current) setIsOpen(false);
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className="w-full h-6 px-1 bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoComplete="off"
              placeholder=""
            />

            {/* Show loading spinner when waiting for cache or actively searching */}
            {(isWaitingForCache || isPreloading || (isLoading && !searchTerm && !value && filteredItems.length === 0)) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                <SmallLoadingSpinner size="xs" />
              </div>
            )}
          </div>

          {/* Render dropdown using portal to avoid table clipping */}
          {isOpen && dropdownPosition && createPortal(
            <ul
              ref={dropdownRef}
              onMouseDown={() => (ignoreBlur.current = true)}
              onMouseUp={() => (ignoreBlur.current = false)}
              onScroll={handleScroll}
              style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 99999
              }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-scroll max-h-[150px]"
              role="listbox"
            >
              {(isLoading || isWaitingForCache) && filteredItems.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-4 w-4 text-blue-500" />
                </li>
              ) : filteredItems.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 bg-gray-50">
                  <div className="text-sm">{t("forms.noResults")}</div>
                </li>
              ) : (
                filteredItems.map((item, index) => (
                  <li
                    key={item._id}
                    className={`px-4 py-2.5 cursor-pointer transition-colors ${
                      highlighted === index ? "bg-blue-50" : "hover:bg-gray-50"
                    } ${value === item._id ? "bg-blue-100 border-l-4 border-blue-500" : ""}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setHighlighted(index)}
                    role="option"
                    aria-selected={value === item._id}
                  >
                    <div className="font-semibold text-gray-900 text-sm">
                      {item.ident}
                    </div>
                  </li>
                ))
              )}
              {/* Loading indicator for infinite scroll — shown at bottom while appending more pages */}
              {isLoadingMore && (
                <li className="px-4 py-2 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-3 w-3 text-blue-500" />
                </li>
              )}
            </ul>,
            document.body
          )}
        </div>
      );
    }

    // Full mode
    return (
      <div className="w-full">
        <div className="relative">
          <div
            className={`flex items-center border rounded-lg bg-white transition-all duration-200 cursor-text ${
              isOpen
                ? "border-blue-500 ring-2 ring-blue-100"
                : "border-gray-300 hover:border-gray-400"
            }`}
            onClick={() => inputRef.current?.focus()}
          >
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon size={18} />
            </div>

            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                if (!disabled) setIsOpen(true);
              }}
              onBlur={() => {
                if (!ignoreBlur.current) setIsOpen(false);
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder=""
              className="w-full py-2 pl-10 pr-10 bg-transparent focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoComplete="off"
            />

            {(value || searchTerm) && (
              <div className="flex items-center pr-2">
                <button
                  onClick={handleClear}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                  aria-label="Clear"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>

          {isOpen && (
            <ul
              ref={dropdownRef}
              onMouseDown={() => (ignoreBlur.current = true)}
              onMouseUp={() => (ignoreBlur.current = false)}
              onScroll={handleScroll}
              className="absolute z-[99999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-scroll max-h-[150px]"
              role="listbox"
            >
              {(isLoading || isWaitingForCache) && filteredItems.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-5 w-5 text-blue-500" />
                </li>
              ) : filteredItems.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 bg-gray-50">{t("forms.noResults")}</li>
              ) : (
                filteredItems.map((item, index) => (
                  <li
                    key={item._id}
                    className={`px-4 py-2.5 cursor-pointer transition-colors ${
                      highlighted === index ? "bg-blue-50" : "hover:bg-gray-50"
                    } ${value === item._id ? "bg-blue-100 border-l-4 border-blue-500" : ""}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setHighlighted(index)}
                    role="option"
                    aria-selected={value === item._id}
                  >
                    <div className="font-medium">{item.ident}</div>
                  </li>
                ))
              )}
              {/* Loading indicator for infinite scroll — shown at bottom while appending more pages */}
              {isLoadingMore && (
                <li className="px-4 py-2 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-4 w-4 text-blue-500" />
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    );
  }
);

export default CachedItemSelectByCode;